import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth, useTheme } from "../App";
import { Button } from "../components/ui/button";
import { ContextHeader } from "../components/ContextHeader";
import {
  TrendingUp, 
  Sun, 
  Moon,
  Menu,
  X,
  Home,
  CreditCard,
  FileCheck,
  CalendarDays,
  Target,
  Trophy,
  BarChart3,
  Sparkles,
  Settings,
  LogOut,
  ChevronDown,
  ArrowUpRight,
  ArrowDownRight,
  ListChecks,
  Cog,
  Heart,
  Link2,
  ArrowLeftRight,
  BookOpen,
  PiggyBank,
  Wallet,
  LayoutGrid,
  ChevronRight,
  Shield,
  TrendingDown,
  Clock,
  Banknote,
  Activity,
  Eye,
  CheckCircle2,
  Flag,
  RefreshCw,
  AlertCircle,
  LayoutDashboard
} from "lucide-react";
import { SetupChecklist } from "../components/guidance/SetupChecklist";
import { WeeklyClosePrompt } from "../components/guidance/WeeklyClosePrompt";
import { NextStepCard } from "../components/guidance/NextStepCard";
import { PlanHealthSignal } from "../components/guidance/PlanHealthSignal";
import { FlowLine } from "../components/guidance/FlowLine";
import { RhythmProgress } from "../components/guidance/RhythmProgress";
import { SharedProgressCard } from "../components/privacy/SharedProgressCard";
import { LatestReflectionCard } from "../components/privacy/LatestReflectionCard";
import { GuidanceBanner } from "../components/guidance/GuidanceBanner";
import { useGuidance } from "../components/guidance/GuidanceProvider";
import BetaFeedbackButton from "../components/BetaFeedbackButton";
import { WeatherCard, WeatherBadge } from "../components/FinancialWeather";
import {
  Utensils,
  Bath,
  HeartHandshake,
  FolderLock,
  PackageCheck,
  MonitorPlay,
  WalletCards,
  PartyPopper,
  Images,
  Wrench,
  ClipboardList,
  ContactRound,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Layout Component
function AppLayout({ children }) {
  const { user, household, households, logout, switchHousehold } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingBillsCount, setPendingBillsCount] = useState(0);

  // Check if user is admin
  const isAdmin = !!user?.is_admin;

  // Fetch pending bills count for subtle indicator
  useEffect(() => {
    const fetchPendingBills = async () => {
      if (!household?.household_id) return;
      
      try {
        // Fetch draft bills
        const billsResp = await fetch(
          `${API_URL}/api/households/${household.household_id}/bills?status=draft`,
          { credentials: 'include' }
        );
        const draftBills = billsResp.ok ? (await billsResp.json()) : [];
        
        // Fetch needs_review imports
        const importsResp = await fetch(
          `${API_URL}/api/households/${household.household_id}/bills/inbox/imports?status=needs_review`,
          { credentials: 'include' }
        );
        const importsData = importsResp.ok ? (await importsResp.json()) : { imports: [] };
        const needsReview = importsData.imports?.filter(i => i.status === 'needs_review') || [];
        
        const total = (Array.isArray(draftBills) ? draftBills.length : 0) + needsReview.length;
        setPendingBillsCount(total);
      } catch (error) {
        // Silently fail - this is just an indicator
        console.debug('Failed to fetch pending bills count:', error);
      }
    };
    
    fetchPendingBills();
    // Refresh every 60 seconds
    const interval = setInterval(fetchPendingBills, 60000);
    return () => clearInterval(interval);
  }, [household?.household_id]);

  const navigation = [
    /* ───── HOME OS ───── */
    { name: "Today's Overview",   href: "/home-os",            icon: Sparkles,       group: "homeos", color: "teal",     subtitle: "Command centre", testId: "nav-home-os" },
    { name: "Family Plan",        href: "/home-os/family-plan", icon: ClipboardList,  group: "homeos", color: "emerald",  subtitle: "Goals & cycle plan" },
    { name: "Schedules",          href: "/home-os/calendar",   icon: CalendarDays,   group: "homeos", color: "indigo",   subtitle: "Calendar & routines" },
    { name: "Financials",         href: "/home-os/money",      icon: WalletCards,    group: "homeos", color: "emerald",  subtitle: "Money, goals, cashflow" },
    { name: "Meals",              href: "/home-os/meals",      icon: Utensils,       group: "homeos", color: "amber",    subtitle: "Plan, cook, shop" },
    { name: "Cleaning",           href: "/home-os/cleaning",   icon: Bath,           group: "homeos", color: "cyan",     subtitle: "Resets & chores" },
    { name: "Maintenance",        href: "/home-os/cleaning",   icon: Wrench,         group: "homeos", color: "sky",      subtitle: "Repairs & upkeep" },
    { name: "Health",             href: "/home-os/health",     icon: Heart,          group: "homeos", color: "rose",     subtitle: "Wellness & care" },
    { name: "Documents",          href: "/home-os/documents",  icon: FolderLock,     group: "homeos", color: "purple",   subtitle: "Admin vault" },
    { name: "Inventory",          href: "/home-os/supplies",   icon: PackageCheck,   group: "homeos", color: "lime",     subtitle: "Pantry & stock" },
    { name: "Contacts",           href: "/home-os/setup",      icon: ContactRound,   group: "homeos", color: "teal",     subtitle: "Household contacts" },
    { name: "Holidays & Events",  href: "/home-os/calendar",   icon: PartyPopper,    group: "homeos", color: "orange",   subtitle: "Birthdays & travel" },
    { name: "Memories & Media",   href: "/home-os/memories",   icon: Images,         group: "homeos", color: "pink",     subtitle: "Photos & albums" },
    { name: "Grateful Tree",      href: "/home-os/mood",       icon: HeartHandshake, group: "homeos", color: "violet",   subtitle: "Family wellbeing" },
    /* ───── ARA Wealth ───── */
    { name: "The Overview",       href: "/dashboard",          icon: Home,           group: "wealth", color: "neutral",  subtitle: "How things look right now" },
    { name: "The Plan",           href: "/plan",               icon: LayoutGrid,     group: "wealth", color: "emerald",  subtitle: "Your goals and spending plan" },
    { name: "The Flow",           href: "/flow",               icon: Activity,       group: "wealth", color: "teal",     subtitle: "Where your money goes" },
    { name: "Review & Reset",     href: "/close",              icon: FileCheck,      group: "wealth", color: "amber",    subtitle: "Review, reflect, move forward" },
  ];

  // Add admin navigation items
  const adminNavigation = isAdmin ? [
    { name: "Vision Panel", href: "/admin/vision", icon: Target, isAdmin: true },
    { name: "Beta Dashboard", href: "/admin/beta", icon: BarChart3, isAdmin: true },
    { name: "Beta Insights", href: "/admin/beta-insights", icon: Eye, isAdmin: true },
  ] : [];

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--whanau-bg-app)' }}>
      {/* Sidebar — glassmorphic Whānau OS shell */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 whanau-glass transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6" style={{ borderBottom: '1px solid var(--whanau-border)' }}>
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--whanau-brand-green)' }}>
                <Shield className="w-5 h-5 text-white" strokeWidth={2.25} />
              </div>
              <div className="leading-tight">
                <span className="block text-[15px] font-extrabold tracking-tight" style={{ color: 'var(--whanau-text-primary)' }}>ARA</span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: 'var(--whanau-text-secondary)' }}>Household OS</span>
              </div>
            </Link>
          </div>

          {/* Household Switcher */}
          {households.length > 0 && (
            <div className="p-4 border-b border-border">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" data-testid="household-switcher">
                    <span className="truncate">{household?.name || 'Select Household'}</span>
                    <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {households.map((hh) => (
                    <DropdownMenuItem 
                      key={hh.household_id}
                      onClick={() => switchHousehold(hh)}
                      className={household?.household_id === hh.household_id ? 'bg-emerald-50 dark:bg-emerald-900/30' : ''}
                    >
                      {hh.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Navigation — HOME OS as the parent system, ARA Wealth as nested module */}
          <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
            {[
              { id: "homeos", label: "Home OS",     hint: "Your household operating system" },
              { id: "wealth", label: "ARA Wealth",  hint: "Financial deep-dive" },
            ].map((group) => {
              const groupItems = navigation.filter((n) => n.group === group.id);
              return (
                <div key={group.id} className="space-y-1">
                  <div className="px-3 pb-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">{group.label}</div>
                    <div className="text-[10px] text-slate-400 dark:text-slate-500 leading-tight">{group.hint}</div>
                  </div>
                  {groupItems.map((item) => {
                    const isActive = window.location.pathname === item.href;
                    const activeColors = {
                      neutral:  'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100',
                      emerald:  'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200',
                      teal:     'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-200',
                      indigo:   'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200',
                      amber:    'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200',
                      cyan:     'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-200',
                      rose:     'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-200',
                      lime:     'bg-lime-50 dark:bg-lime-900/30 text-lime-700 dark:text-lime-200',
                      orange:   'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-200',
                      pink:     'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-200',
                      violet:   'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-200',
                      purple:   'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-200',
                      slate:    'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200',
                    };
                    const dotColors = {
                      neutral: 'bg-slate-400', emerald: 'bg-emerald-500', teal: 'bg-teal-500',
                      indigo: 'bg-indigo-500', amber: 'bg-amber-500', cyan: 'bg-cyan-500',
                      rose: 'bg-rose-500', lime: 'bg-lime-500', orange: 'bg-orange-500',
                      pink: 'bg-pink-500',
                      violet: 'bg-violet-500', purple: 'bg-purple-600', slate: 'bg-slate-500',
                    };
                    const iconColors = {
                      neutral: 'text-slate-600 dark:text-slate-300',
                      emerald: 'text-emerald-600 dark:text-emerald-300',
                      teal: 'text-teal-600 dark:text-teal-300',
                      indigo: 'text-indigo-600 dark:text-indigo-300',
                      amber: 'text-amber-600 dark:text-amber-300',
                      cyan: 'text-cyan-600 dark:text-cyan-300',
                      rose: 'text-rose-600 dark:text-rose-300',
                      lime: 'text-lime-600 dark:text-lime-300',
                      orange: 'text-orange-600 dark:text-orange-300',
                      pink: 'text-pink-600 dark:text-pink-300',
                      violet: 'text-violet-600 dark:text-violet-300',
                      purple: 'text-purple-700 dark:text-purple-300',
                      slate: 'text-slate-600 dark:text-slate-300',
                    };
                    return (
                      <Link
                        key={item.name + item.href}
                        to={item.href}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                          isActive
                            ? activeColors[item.color] || activeColors.neutral
                            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/70 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white'
                        }`}
                        data-testid={item.testId || `nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? dotColors[item.color] || 'bg-slate-400' : 'bg-transparent'}`} />
                        <item.icon className={`w-[18px] h-[18px] shrink-0 ${isActive ? iconColors[item.color] || '' : 'text-slate-500 dark:text-slate-400'}`} strokeWidth={isActive ? 2.5 : 2} />
                        <div className="flex-1 min-w-0">
                          <span className="text-[13px] font-semibold leading-tight block truncate">{item.name}</span>
                          <p className="text-[10px] text-slate-400 dark:text-slate-500 leading-tight truncate">{item.subtitle}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              );
            })}
            
            {/* Settings link */}
            <div className="mt-4 pt-3 border-t border-border/50">
              <Link
                to="/settings"
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                  window.location.pathname === '/settings'
                    ? 'bg-slate-100 dark:bg-slate-800 text-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid="nav-settings"
              >
                <Settings className="w-4 h-4" />
                Settings
              </Link>
              <Link
                to="/integrations"
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                  window.location.pathname === '/integrations'
                    ? 'bg-slate-100 dark:bg-slate-800 text-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid="nav-integrations"
              >
                <Link2 className="w-4 h-4" />
                Integrations
              </Link>
            </div>

            {/* Admin Navigation */}
            {adminNavigation.length > 0 && (
              <>
                <div className="mt-4 mb-2 px-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Admin</p>
                </div>
                {adminNavigation.map((item) => {
                  const isActive = window.location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive 
                          ? 'bg-[#1a4d3e]/10 text-[#1a4d3e] dark:bg-[#1a4d3e]/30 dark:text-teal-300' 
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          {/* User Menu */}
          <div className="p-4 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start gap-3 px-2" data-testid="user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-700">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={toggleTheme}>
                  {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                  Toggle Theme
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-toggle">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="mobile-logo">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--whanau-brand-green)' }}>
            <Shield className="w-4 h-4 text-white" strokeWidth={2.25} />
          </div>
          <div className="leading-tight">
            <span className="block text-[13px] font-extrabold tracking-tight" style={{ color: 'var(--whanau-text-primary)' }}>ARA</span>
            <span className="block text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: 'var(--whanau-text-secondary)' }}>Household OS</span>
          </div>
        </Link>
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>

      {/* Backdrop */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-w-0 lg:ml-64 pt-16 lg:pt-0">
        {children}
      </main>
    </div>
  );
}

// ============== HELPER FUNCTIONS ==============

// Get current week boundaries (Monday-Sunday)
function getCurrentWeekBoundaries() {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);
  monday.setHours(0, 0, 0, 0);
  
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  
  return {
    week_start: monday.toISOString().split('T')[0],
    week_end: sunday.toISOString().split('T')[0],
    monday,
    sunday
  };
}

// Format currency helper
function formatCurrency(amount, currency = 'NZD') {
  return new Intl.NumberFormat('en-NZ', { 
    style: 'currency', 
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 
  }).format(amount || 0);
}

// Format date helper
function formatDate(date, options = { month: 'short', day: 'numeric' }) {
  return new Date(date).toLocaleDateString('en-NZ', options);
}

// Dashboard Page
export default function DashboardPage() {
  const { user, household } = useAuth();
  const { state: guidance } = useGuidance();
  const currency = household?.currency || 'NZD';
  const firstName = (user?.name || "").split(" ")[0] || "there";
  
  // State
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [incomeData, setIncomeData] = useState(null);
  const [allocationData, setAllocationData] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [reviewQueueCount, setReviewQueueCount] = useState(0);
  const [weeklyCloseData, setWeeklyCloseData] = useState(null);
  const [calmScore, setCalmScore] = useState(null);
  const [financialWeather, setFinancialWeather] = useState(null);
  const [showCalmDetails, setShowCalmDetails] = useState(false);
  const [monthlySnapshot, setMonthlySnapshot] = useState({ income: 0, spending: 0 });
  const [nextIncomeEvent, setNextIncomeEvent] = useState(null);
  const [upcomingBills, setUpcomingBills] = useState([]);
  const [activeGoals, setActiveGoals] = useState([]);
  const [feedHealth, setFeedHealth] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!household) return;
    
    const fetchDashboardData = async () => {
      const { week_start, week_end } = getCurrentWeekBoundaries();
      
      try {
        // Fetch all data in parallel
        const [dashRes, incomeRes, allocRes, txRes, closeRes, calmRes, weatherRes] = await Promise.all([
          fetch(`${API_URL}/api/households/${household.household_id}/dashboard`, { credentials: 'include' }),
          fetch(`${API_URL}/api/households/${household.household_id}/income/weekly-check?week_start=${week_start}`, { credentials: 'include' }),
          fetch(`${API_URL}/api/households/${household.household_id}/allocations/weekly-check?week_start=${week_start}&week_end=${week_end}`, { credentials: 'include' }),
          fetch(`${API_URL}/api/households/${household.household_id}/transactions?limit=5`, { credentials: 'include' }),
          fetch(`${API_URL}/api/weekly-close/latest`, { credentials: 'include' }),
          fetch(`${API_URL}/api/households/${household.household_id}/calm-score`, { credentials: 'include' }),
          fetch(`${API_URL}/api/households/${household.household_id}/cashflow/weather`, { credentials: 'include' })
        ]);
        
        if (dashRes.ok) {
          const data = await dashRes.json();
          setDashboardData(data);
          // Get recent transactions from dashboard data if available
          if (data.recent_transactions) {
            setRecentTransactions(data.recent_transactions);
          }
        }
        if (incomeRes.ok) {
          const data = await incomeRes.json();
          setIncomeData(data.this_week || data);
        }
        if (allocRes.ok) setAllocationData(await allocRes.json());
        if (txRes.ok) {
          const txData = await txRes.json();
          // Handle both array and object response
          const transactions = Array.isArray(txData) ? txData : (txData.transactions || []);
          if (transactions.length > 0) {
            setRecentTransactions(transactions);
          }
        }
        if (closeRes.ok) setWeeklyCloseData(await closeRes.json());
        if (calmRes.ok) setCalmScore(await calmRes.json());
        if (weatherRes.ok) setFinancialWeather(await weatherRes.json());
        
        // Fetch uncategorized transaction count separately (for review queue)
        try {
          const uncatRes = await fetch(
            `${API_URL}/api/households/${household.household_id}/transactions?status=pending&limit=1`,
            { credentials: 'include' }
          );
          if (uncatRes.ok) {
            const uncatData = await uncatRes.json();
            const count = Array.isArray(uncatData) ? uncatData.length : (uncatData.count || 0);
            // If we got exactly 1 result with limit=1, there may be more
            setReviewQueueCount(count > 0 ? count : 0);
          }
        } catch (e) {
          // Silently fail - review queue is optional
        }
        
        // Fetch Financial Snapshot data
        try {
          const now = new Date();
          const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
          const monthEnd = now.toISOString().split('T')[0];
          const in7Days = new Date(now.getTime() + 7 * 86400000).toISOString().split('T')[0];
          
          const [monthTxRes, nextIncRes, billsRes, reviewRes, goalsRes] = await Promise.all([
            fetch(`${API_URL}/api/households/${household.household_id}/transactions?start_date=${monthStart}&end_date=${monthEnd}&limit=500`, { credentials: 'include' }),
            fetch(`${API_URL}/api/households/${household.household_id}/income/events?status=expected&limit=5`, { credentials: 'include' }),
            fetch(`${API_URL}/api/households/${household.household_id}/bills`, { credentials: 'include' }),
            fetch(`${API_URL}/api/households/${household.household_id}/transactions?status=imported&limit=1`, { credentials: 'include' }),
            fetch(`${API_URL}/api/households/${household.household_id}/workflow-goals?cycle=weekly`, { credentials: 'include' }),
          ]);
          
          // Monthly income vs spending
          if (monthTxRes.ok) {
            const mData = await monthTxRes.json();
            const txns = Array.isArray(mData) ? mData : (mData.transactions || []);
            let income = 0, spending = 0;
            for (const t of txns) {
              if (t.amount > 0) income += t.amount;
              else spending += Math.abs(t.amount);
            }
            setMonthlySnapshot({ income, spending });
          }
          
          // Next expected income event
          if (nextIncRes.ok) {
            const eData = await nextIncRes.json();
            const events = eData.events || [];
            const today = now.toISOString().split('T')[0];
            const future = events
              .filter(e => e.expected_date >= today)
              .sort((a, b) => a.expected_date.localeCompare(b.expected_date));
            setNextIncomeEvent(future[0] || null);
          }
          
          // Bills due in next 7 days
          if (billsRes.ok) {
            const bData = await billsRes.json();
            const allBills = Array.isArray(bData) ? bData : (bData.bills || []);
            const today = now.toISOString().split('T')[0];
            const due = allBills.filter(b => b.next_due_date && b.next_due_date >= today && b.next_due_date <= in7Days);
            setUpcomingBills(due);
          }
          
          // Review queue count
          if (reviewRes.ok) {
            const rData = await reviewRes.json();
            const total = rData.total || rData.count || 0;
            if (total > 0) setReviewQueueCount(total);
          }
          
          // Active weekly goals
          if (goalsRes.ok) {
            const gData = await goalsRes.json();
            setActiveGoals(gData.goals || []);
          }
        } catch (e) {
          // Snapshot data is optional
        }
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    // Feed health check
    fetch(`${API_URL}/api/connections/health`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setFeedHealth(d); })
      .catch(() => {});
  }, [household]);

  // Calculate available cash (liquid accounts)
  const availableCash = (dashboardData?.accounts || [])
    .filter(a => a.account_type === 'checking' || a.account_type === 'savings')
    .reduce((sum, a) => sum + (a.balance || 0), 0);

  // Get review & reset status
  const getWeeklyCloseStatus = () => {
    const { week_start } = getCurrentWeekBoundaries();
    const lastCloseDate = weeklyCloseData?.week_start;
    
    if (lastCloseDate === week_start && weeklyCloseData?.status === 'finalized') {
      return { status: 'complete', lastClose: lastCloseDate };
    }
    return { status: 'not_started', lastClose: lastCloseDate || null };
  };

  const weeklyClose = getWeeklyCloseStatus();
  const { monday, sunday } = getCurrentWeekBoundaries();

  // Calculate next income date
  const getNextIncomeDate = () => {
    if (!incomeData?.next_income_date) return null;
    return incomeData.next_income_date;
  };

  // Get top allocation categories
  const topAllocations = (allocationData?.groups || [])
    .filter(g => g.allocated > 0)
    .sort((a, b) => b.allocated - a.allocated)
    .slice(0, 3);

  // Detect first-time/empty household — show guide until user completes at least one setup step
  const hasGoals = activeGoals.length > 0;
  const hasIncome = incomeData && incomeData.expected_total > 0;
  const hasAllocations = allocationData && allocationData.total_allocated > 0;
  const hasTransactions = recentTransactions.length > 0;
  const isEmptyHousehold = !loading && !hasGoals && !hasIncome && !hasAllocations && !hasTransactions;

  const handleFeedRefresh = async () => {
    if (!household?.household_id) return;
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/akahu/households/${household.household_id}/sync`, { method: "POST", credentials: "include" });
      if (res.ok) toast.success("Sync triggered — data will refresh shortly");
      else toast.error("Sync couldn't complete — try reconnecting in Settings");
    } catch { toast.error("Couldn't reach the sync service"); }
    finally { setRefreshing(false); }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="w-10 h-10 border-3 border-[#1a4d3e] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  /* ─── Derived values ─── */
  const flowGroups = (allocationData?.groups || []).filter(g => g.allocated > 0).sort((a, b) => b.allocated - a.allocated);
  const totalPlanned = flowGroups.reduce((s, g) => s + (g.allocated || 0), 0);
  const totalActual  = flowGroups.reduce((s, g) => s + (g.actual   || 0), 0);
  const flowMax      = Math.max(totalPlanned, totalActual, 1);
  const barColors    = ['bg-[#1a4d3e]', 'bg-[#8b6914]', 'bg-[#6b5a3d]', 'bg-[#7c6f64]'];
  const barLights    = ['bg-[#1a4d3e]/25', 'bg-[#8b6914]/25', 'bg-[#6b5a3d]/20', 'bg-[#7c6f64]/20'];
  const topGoal      = activeGoals[0] || null;
  const goalPct      = topGoal && topGoal.target_amount > 0 ? Math.min(100, Math.round((topGoal.supported_total / topGoal.target_amount) * 100)) : null;
  const calmLabel    = calmScore?.interpretation?.level || 'Calculating';
  const calmMsg      = calmScore?.interpretation?.message || '';
  const monthNet     = monthlySnapshot.income - monthlySnapshot.spending;

  return (
    <AppLayout>
      <div data-testid="dashboard-page" className="whanau-app">
        {/* ── Private household briefing — hero ── */}
        <div className="px-4 lg:px-6 pt-5 pb-3">
          <div className="whanau-kia-ora flex items-start justify-between gap-4 flex-wrap" data-testid="dashboard-kia-ora">
            <div className="min-w-0 flex-1 flex items-start gap-3">
              <div className="hidden sm:flex w-11 h-11 rounded-xl items-center justify-center flex-shrink-0" style={{ background: 'var(--whanau-bg-card-subtle)', border: '1px solid var(--whanau-border)' }} aria-hidden>
                <Shield className="w-5 h-5" strokeWidth={2.25} style={{ color: 'var(--whanau-brand-green)' }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="whanau-label" style={{ color: 'var(--whanau-text-secondary)', letterSpacing: '0.16em' }}>Private household overview</p>
                <h1 className="whanau-h1 text-3xl sm:text-4xl mt-1.5" data-testid="dashboard-greeting">
                  Kia ora, {firstName}
                </h1>
                <p className="whanau-body text-sm sm:text-base mt-1 max-w-xl">
                  Your household position is current and ready for review.
                </p>
                <p
                  className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wide"
                  style={{ color: 'var(--whanau-text-muted)' }}
                  data-testid="dashboard-prepared-by-ara"
                >
                  <Shield className="w-3 h-3" strokeWidth={2.25} style={{ color: 'var(--whanau-gold)' }} />
                  <span>Prepared by ARA · Updated {new Date().toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit' })}</span>
                </p>
              </div>
            </div>
            <Link
              to="/plan"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-sm transition-colors"
              style={{ background: 'var(--whanau-brand-green)', color: '#FFFFFF' }}
              data-testid="dashboard-review-plan-link"
            >
              Review your plan
              <ChevronRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </Link>
          </div>
        </div>

      <div className="px-4 lg:px-6 pb-6 max-w-6xl space-y-3">

        {/* ── Feed Health Banner ── */}
        {feedHealth && feedHealth.status !== "healthy" && (
          <div className={`rounded-lg px-3.5 py-2.5 flex items-center justify-between gap-3 ${feedHealth.status === "stale" ? "bg-amber-50 dark:bg-amber-900/20" : feedHealth.status === "error" ? "bg-red-50/60 dark:bg-red-900/20" : "bg-slate-100 dark:bg-slate-800"}`} data-testid="dashboard-feed-banner">
            <div className="flex items-center gap-2">
              <AlertCircle className={`w-3.5 h-3.5 flex-shrink-0 ${feedHealth.status === "stale" ? "text-amber-600" : "text-slate-500"}`} />
              <p className={`text-sm font-medium ${feedHealth.status === "stale" ? "text-amber-700 dark:text-amber-300" : "text-slate-600"}`}>{feedHealth.message}</p>
            </div>
            <button onClick={handleFeedRefresh} disabled={refreshing} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-slate-200 dark:border-slate-700 text-[11px] font-medium hover:bg-white dark:hover:bg-slate-700 transition-colors flex-shrink-0">
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />Refresh
            </button>
          </div>
        )}

        {/* ════════════════════════════════════════════
            1. PRIMARY: MONEY FLOW (FIRST VISUAL)
           ════════════════════════════════════════════ */}
        <section className="whanau-card" data-testid="money-flow-bar" style={{ padding: '1.25rem' }}>
          <p className="whanau-label mb-3">This cycle's flow</p>

          {totalPlanned === 0 && totalActual === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-foreground/70">No plan or spending data yet</p>
              <Link to="/plan?tab=expenses" className="text-xs text-[#1a4d3e] hover:underline mt-1 inline-block font-medium">Set up your plan</Link>
            </div>
          ) : (
            <>
              {/* Planned bar */}
              <div className="mb-2">
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium text-foreground/70">Planned</span>
                  <span className="font-semibold tabular-nums text-foreground">{formatCurrency(totalPlanned, currency)}</span>
                </div>
                <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded flex overflow-hidden">
                  {flowGroups.map((g, i) => (
                    <div key={g.name} className={`h-full ${barLights[i] || barLights[3]} transition-all`} style={{ width: `${(g.allocated / flowMax) * 100}%` }} title={`${g.name}: ${formatCurrency(g.allocated, currency)}`} />
                  ))}
                </div>
              </div>

              {/* Actual bar (solid overlay) */}
              <div className="mb-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium text-foreground/70">Actual</span>
                  <span className="font-semibold tabular-nums text-foreground">{formatCurrency(totalActual, currency)}</span>
                </div>
                <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded flex overflow-hidden">
                  {flowGroups.map((g, i) => (
                    <div key={g.name} className={`h-full ${barColors[i] || barColors[3]} transition-all`} style={{ width: `${((g.actual || 0) / flowMax) * 100}%` }} title={`${g.name}: ${formatCurrency(g.actual || 0, currency)}`} />
                  ))}
                </div>
              </div>

              {/* Legend + remaining */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex gap-3">
                  {flowGroups.slice(0, 3).map((g, i) => (
                    <div key={g.name} className="flex items-center gap-1.5 text-xs">
                      <span className={`w-2 h-2 rounded-sm ${barColors[i] || barColors[3]}`} />
                      <span className="text-foreground/60">{g.name}</span>
                    </div>
                  ))}
                </div>
                <p className={`text-xs font-semibold tabular-nums ${totalPlanned - totalActual >= 0 ? 'text-[#1a4d3e]' : 'text-amber-600'}`}>
                  {formatCurrency(Math.abs(totalPlanned - totalActual), currency)} {totalPlanned - totalActual >= 0 ? 'remaining' : 'over'}
                </p>
              </div>
            </>
          )}
        </section>

        {/* ════════════════════════════════════════════
            2. GOAL + REFLECTION (SIDE BY SIDE)
           ════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Current Goal */}
          <div className="bg-card border border-border rounded-xl p-4 flex items-start justify-between" data-testid="current-goal-card">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-lg bg-[#1a4d3e]/10 flex items-center justify-center"><Flag className="w-3 h-3 text-[#1a4d3e]" /></div>
                <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wide">Current Goal</p>
              </div>
              {topGoal ? (
                <>
                  <p className="text-sm font-semibold text-foreground truncate mb-1" data-testid="goal-name">{topGoal.description}</p>
                  {goalPct !== null && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full bg-[#1a4d3e] rounded-full transition-all" style={{ width: `${goalPct}%` }} />
                      </div>
                      <span className="text-xs font-semibold tabular-nums text-[#1a4d3e]">{goalPct}%</span>
                    </div>
                  )}
                  <p className={`text-[11px] font-medium mt-1 ${goalPct >= 80 ? 'text-[#1a4d3e]' : goalPct >= 40 ? 'text-amber-600' : 'text-foreground/50'}`}>
                    {goalPct >= 80 ? 'On track' : goalPct >= 40 ? 'Slight drift' : 'Needs attention'}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-foreground/60">No goal set this week</p>
                  <Link to="/plan" className="text-xs text-[#1a4d3e] hover:underline font-medium">Set your first goal</Link>
                </>
              )}
            </div>
            {activeGoals.length > 1 && <Link to="/plan" className="text-[10px] text-[#1a4d3e] hover:underline flex-shrink-0 ml-2">+{activeGoals.length - 1} more</Link>}
          </div>

          {/* Latest Reflection */}
          <div className="bg-card border border-border rounded-xl p-4" data-testid="latest-reflection-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-lg bg-[#1a4d3e]/10 flex items-center justify-center"><BookOpen className="w-3 h-3 text-[#1a4d3e]" /></div>
              <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wide">Latest Reflection</p>
            </div>
            <LatestReflectionCard inline={true} />
          </div>
        </div>

        {/* ════════════════════════════════════════════
            3. FOUR CORE STATUS CARDS (2×2 GRID)
           ════════════════════════════════════════════ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="status-cards-grid">

          {/* This Month */}
          <div className="bg-card border border-border rounded-xl p-3.5" data-testid="monthly-overview-card">
            <div className="flex items-center gap-1.5 mb-2">
              <Banknote className="w-3.5 h-3.5 text-[#1a4d3e]" />
              <h3 className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-wide">This Month</h3>
            </div>
            {monthlySnapshot.income === 0 && monthlySnapshot.spending === 0 ? (
              <p className="text-xs text-foreground/50">Waiting for data</p>
            ) : (
              <>
                <p className={`text-lg font-bold tabular-nums ${monthNet >= 0 ? 'text-[#1a4d3e]' : 'text-amber-600'}`} data-testid="monthly-net">
                  {monthNet >= 0 ? '+' : ''}{formatCurrency(monthNet, currency)}
                </p>
                <div className="flex justify-between text-[11px] text-foreground/50 mt-1">
                  <span>In: {formatCurrency(monthlySnapshot.income, currency)}</span>
                  <span>Out: {formatCurrency(monthlySnapshot.spending, currency)}</span>
                </div>
              </>
            )}
          </div>

          {/* Coming Up */}
          <div className="bg-card border border-border rounded-xl p-3.5" data-testid="upcoming-cashflow-card">
            <div className="flex items-center gap-1.5 mb-2">
              <Clock className="w-3.5 h-3.5 text-[#1a4d3e]" />
              <h3 className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-wide">Coming Up</h3>
            </div>
            {nextIncomeEvent ? (
              <>
                <p className="text-sm font-semibold text-[#1a4d3e] tabular-nums" data-testid="next-income-name">+{formatCurrency(nextIncomeEvent.expected_amount || 0, currency)}</p>
                <p className="text-[11px] text-foreground/50 truncate">{nextIncomeEvent.source_name || 'Income'} · {formatDate(nextIncomeEvent.expected_date, { weekday: 'short', month: 'short', day: 'numeric' })}</p>
              </>
            ) : (
              <p className="text-xs text-foreground/50">No income scheduled</p>
            )}
            {upcomingBills.length > 0 && (
              <p className="text-[11px] text-amber-600 font-medium mt-1.5">{upcomingBills.length} bill{upcomingBills.length !== 1 ? 's' : ''} due this week</p>
            )}
          </div>

          {/* Needs a Look */}
          <div className="bg-card border border-border rounded-xl p-3.5" data-testid="needs-attention-card">
            <div className="flex items-center gap-1.5 mb-2">
              <ListChecks className="w-3.5 h-3.5 text-amber-600" />
              <h3 className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-wide">Needs a Look</h3>
            </div>
            {reviewQueueCount > 0 ? (
              <>
                <p className="text-lg font-bold text-amber-600 tabular-nums" data-testid="review-count">{reviewQueueCount}</p>
                <p className="text-[11px] text-foreground/50 mb-2">transaction{reviewQueueCount !== 1 ? 's' : ''} to review</p>
                <Link to="/close" className="text-[11px] font-medium text-amber-600 hover:underline" data-testid="go-to-close-btn">Review now</Link>
              </>
            ) : (
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-[#1a4d3e]" />
                <p className="text-sm font-medium text-[#1a4d3e]" data-testid="all-clear-message">All clear</p>
              </div>
            )}
          </div>

          {/* Calm Score — Whānau gauge */}
          <div className="whanau-tile p-4" data-testid="calm-score-card" style={{ background: 'linear-gradient(135deg, #FFFFFF 0%, var(--whanau-money-soft) 140%)' }}>
            <div className="flex items-center gap-1.5 mb-2">
              <span className="whanau-icon-bubble" style={{ background: 'var(--whanau-money-soft)', color: 'var(--whanau-money)', width: 28, height: 28, borderRadius: 10 }}>
                <Activity className="w-3.5 h-3.5" />
              </span>
              <h3 className="whanau-label" style={{ color: 'var(--whanau-money)' }}>Calm score</h3>
            </div>
            {calmScore ? (
              <>
                <div className="flex items-end gap-2">
                  <p className="whanau-data text-3xl" data-testid="calm-score-value" style={{ color: 'var(--whanau-money)' }}>{calmScore.score}</p>
                  <span className="text-xs font-semibold pb-1" style={{ color: 'var(--whanau-text-muted)' }}>/100</span>
                  <span className="ml-auto whanau-pill text-[10px]" style={{
                    background: calmLabel === 'Excellent' || calmLabel === 'Good' ? 'var(--whanau-money-soft)' :
                                calmLabel === 'Building' ? 'var(--whanau-meals-soft)' : 'var(--whanau-bg-card-subtle)',
                    color: calmLabel === 'Excellent' || calmLabel === 'Good' ? 'var(--whanau-money)' :
                           calmLabel === 'Building' ? 'var(--whanau-meals)' : 'var(--whanau-text-secondary)',
                  }}>{calmLabel}</span>
                </div>
                {/* Gauge bar */}
                <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: 'var(--whanau-bg-card-subtle)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, Math.max(0, calmScore.score))}%`,
                      background: 'var(--whanau-grad-money)',
                    }}
                    data-testid="calm-score-gauge"
                  />
                </div>
                <p className="whanau-body text-xs mt-2 line-clamp-2">{calmMsg}</p>
              </>
            ) : (
              <p className="text-xs whanau-body">Calculating…</p>
            )}
          </div>
        </div>

        {/* ════════════════════════════════════════════
            4. HOW THINGS FEEL (COMPACT INSIGHT)
           ════════════════════════════════════════════ */}
        {financialWeather && (
          <section data-testid="financial-weather-section">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xs font-bold text-muted-foreground/60 uppercase tracking-wide">How Things Feel</h2>
                <Link to="/flow" className="text-[11px] text-[#1a4d3e] hover:underline flex items-center gap-0.5">View timeline <ChevronRight className="w-3 h-3" /></Link>
              </div>
              <WeatherCard weather={financialWeather} currency={currency} showActions={financialWeather.status !== 'calm'} compact={true} />
            </div>
          </section>
        )}

        {/* ════════════════════════════════════════════
            5. INCOME + PLAN (SIDE BY SIDE, COMPACT)
           ════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Money In */}
          <div className="bg-card border border-border rounded-xl p-4" data-testid="income-status-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-[#1a4d3e]" /><h2 className="text-sm font-semibold text-foreground">Money In</h2></div>
              <Link to="/plan?tab=income" className="text-[11px] text-[#1a4d3e] hover:underline">Manage</Link>
            </div>
            {!incomeData || (!incomeData.expected_total && !incomeData.received_total) ? (
              <div className="text-center py-2">
                <p className="text-sm text-foreground/60">Not set up yet</p>
                <Link to="/plan?tab=income" className="text-xs text-[#1a4d3e] hover:underline font-medium">Set it up</Link>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-foreground/60">Expected</span><span className="font-medium tabular-nums">{formatCurrency(incomeData?.expected_total || 0, currency)}</span></div>
                <div className="flex justify-between"><span className="text-foreground/60">Received</span><span className="font-medium text-[#1a4d3e] tabular-nums">{formatCurrency(incomeData?.received_total || 0, currency)}</span></div>
                <div className="flex justify-between pt-1.5 border-t border-border"><span className="text-foreground/60">Difference</span><span className={`font-semibold tabular-nums ${(incomeData?.variance || 0) >= 0 ? 'text-[#1a4d3e]' : 'text-amber-600'}`}>{(incomeData?.variance || 0) >= 0 ? '+' : ''}{formatCurrency(incomeData?.variance || 0, currency)}</span></div>
              </div>
            )}
          </div>

          {/* Your Plan */}
          <div className="bg-card border border-border rounded-xl p-4" data-testid="plan-status-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><LayoutGrid className="w-4 h-4 text-[#1a4d3e]" /><h2 className="text-sm font-semibold text-foreground">Your Plan</h2></div>
              <Link to="/plan" className="text-[11px] text-[#1a4d3e] hover:underline">Edit</Link>
            </div>
            {!allocationData || (!allocationData.total_allocated && !allocationData.remaining_budget) ? (
              <div className="text-center py-2">
                <p className="text-sm text-foreground/60">Not set up yet</p>
                <Link to="/plan?tab=expenses" className="text-xs text-[#1a4d3e] hover:underline font-medium">Set it up</Link>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-foreground/60">Planned</span><span className="font-medium tabular-nums">{formatCurrency(allocationData?.total_allocated || 0, currency)}</span></div>
                <div className="flex justify-between"><span className="text-foreground/60">Left to plan</span><span className={`font-medium tabular-nums ${(allocationData?.remaining_budget || 0) >= 0 ? 'text-[#1a4d3e]' : 'text-amber-600'}`}>{formatCurrency(allocationData?.remaining_budget || 0, currency)}</span></div>
                {topAllocations.length > 0 && (
                  <div className="pt-1.5 border-t border-border space-y-1">
                    {topAllocations.map((group, i) => (
                      <div key={i} className="flex justify-between text-xs"><span className="text-foreground/50 truncate max-w-[140px]">{group.name}</span><span className="tabular-nums">{formatCurrency(group.allocated, currency)}</span></div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ════════════════════════════════════════════
            6. RECENT ACTIVITY (COMPACT)
           ════════════════════════════════════════════ */}
        <section data-testid="money-activity-card">
          <div className="whanau-card" style={{ padding: '1.25rem' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="whanau-icon-bubble" style={{ background: 'var(--whanau-money-soft)', color: 'var(--whanau-money)', width: 32, height: 32, borderRadius: 12 }}>
                  <CreditCard className="w-4 h-4" />
                </span>
                <h2 className="whanau-h3 text-base">Recent activity</h2>
              </div>
              <div className="flex items-center gap-2">
                {reviewQueueCount > 0 && (
                  <Link
                    to="/close"
                    className="whanau-pill text-[11px]"
                    style={{ background: 'var(--whanau-meals-soft)', color: 'var(--whanau-meals)' }}
                    data-testid="review-queue-link"
                  >{reviewQueueCount} to review</Link>
                )}
                <Link to="/flow" className="text-xs font-semibold hover:underline" style={{ color: 'var(--whanau-money)' }}>See all →</Link>
              </div>
            </div>
            {recentTransactions.length > 0 ? (
              <div className="space-y-0">
                {recentTransactions.slice(0, 5).map((tx) => (
                  <div key={tx.transaction_id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${tx.amount < 0 ? 'bg-slate-100 dark:bg-slate-800' : 'bg-[#1a4d3e]/10'}`}>
                        {tx.amount < 0 ? <ArrowUpRight className="w-3.5 h-3.5 text-foreground/50" /> : <ArrowDownRight className="w-3.5 h-3.5 text-[#1a4d3e]" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{tx.description}</p>
                        <p className="text-[11px] text-foreground/40">{tx.category || 'Uncategorized'} · {formatDate(tx.date)}</p>
                      </div>
                    </div>
                    <p className={`text-sm font-semibold tabular-nums flex-shrink-0 ml-2 ${tx.amount >= 0 ? 'text-[#1a4d3e]' : 'text-foreground'}`}>{tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount, currency)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-foreground/50">Nothing here yet</p>
                <p className="text-xs text-foreground/30 mt-0.5">Spending shows up once your bank is connected.</p>
              </div>
            )}
          </div>
        </section>

        {/* ════════════════════════════════════════════
            7. REVIEW & RESET (COMPACT)
           ════════════════════════════════════════════ */}
        <section data-testid="weekly-close-card">
          <div className="bg-[#1a4d3e]/5 dark:bg-[#1a4d3e]/10 border border-[#1a4d3e]/20 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-[#1a4d3e]/10 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-4 h-4 text-[#1a4d3e]" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-foreground">Review & Reset</h2>
                  <p className="text-[11px] text-foreground/50" data-testid="weekly-reset-message">
                    {weeklyClose.status === 'complete' ? "You're up to date." : "Clear things up and move forward."}
                  </p>
                </div>
              </div>
              <Link to="/close?tab=close-cycle">
                <Button size="sm" className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90 text-white rounded-full text-xs px-4" data-testid="weekly-close-btn">
                  {weeklyClose.status === 'complete' ? 'View' : 'Start'}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* ── Shared Progress & Rhythm ── */}
        <SharedProgressCard />
        <RhythmProgress />

      </div>
      </div>
      <BetaFeedbackButton />
    </AppLayout>
  );
}

export { AppLayout };
